import { useCallback, useEffect, useState } from 'react';
import { Table, Button, Loader, Alert, Dialog, Input, Textarea, Select, Switch } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { MODEL_EDITORS } from '@/admin/models';

const HIDDEN = ['password', 'token_hash'];
const errorText = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message;

const initialValues = (editor, row) =>
  Object.fromEntries(
    editor.fields.map((f) => {
      const current = row ? row[f.name] : f.defaultValue;
      if (f.type === 'switch') return [f.name, row ? Boolean(current) : Boolean(f.defaultValue)];
      if (f.type === 'date') return [f.name, current ? String(current).slice(0, 10) : ''];
      return [f.name, current ?? ''];
    }),
  );

function FieldInput({ field, value, onChange }) {
  const common = { label: field.label, disabled: field.readOnly };
  if (field.type === 'textarea') return <Textarea {...common} rows={3} value={value} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === 'select') return <Select {...common} options={field.options} value={value} onChange={onChange} />;
  if (field.type === 'switch') return <Switch checked={value} onChange={onChange} rightLabel={field.label} ariaLabel={field.label} />;
  return (
    <Input
      {...common}
      type={field.type || 'text'}
      required={field.required}
      placeholder={field.placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * List page for any model. Models with an entry in admin/models.js get create,
 * edit and delete; everything else stays a read-only table.
 */
export default function CrudPage({ model, module, title }) {
  const { fetchApi } = useAppApi();
  const editor = MODEL_EDITORS[model];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    return fetchApi('crud', { model, action: 'list', limit: 200 })
      .then((res) => {
        const list = Array.isArray(res) ? res : (res && res.data) || [];
        setRows([...list].sort((a, b) => (b.id || 0) - (a.id || 0)));
      })
      .catch((ex) => setErr(errorText(ex)))
      .finally(() => setLoading(false));
  }, [model]);

  useEffect(() => {
    load();
  }, [load]);

  const keys = rows[0] ? Object.keys(rows[0]).filter((k) => !HIDDEN.includes(k)) : [];
  const shown = editor ? editor.columns.filter((k) => !rows[0] || keys.includes(k)) : keys;
  const columns = shown.map((k) => ({ key: k, label: k.replace(/_/g, ' ') }));
  const tableRows = rows.map((r) => ({ ...r, is_published: r.is_published ? 'Yes' : 'No', is_active: r.is_active ? 'Yes' : 'No' }));

  const openForm = (row) => {
    setFormErr('');
    setConfirmingDelete(false);
    setValues(initialValues(editor, row));
    setEditing(row || {});
  };

  const save = async () => {
    setSaving(true);
    setFormErr('');
    try {
      const data = Object.fromEntries(
        editor.fields
          .filter((f) => !f.readOnly)
          .map((f) => {
            const v = values[f.name];
            if (f.type === 'number') return [f.name, Number(v) || 0];
            if (f.type === 'date') return [f.name, v ? new Date(v).toISOString() : null];
            return [f.name, v === '' ? null : v];
          }),
      );
      if (editing.id) await fetchApi('crud', { model, action: 'update', where: { id: editing.id }, data: { ...data, updated_at: new Date().toISOString() } });
      else await fetchApi('crud', { model, action: 'create', data });
      setEditing(null);
      await load();
    } catch (ex) {
      setFormErr(errorText(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing.id) return;
    setSaving(true);
    try {
      await fetchApi('crud', { model, action: 'delete', where: { id: editing.id } });
      setEditing(null);
      await load();
    } catch (ex) {
      setFormErr(errorText(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold">{(editor && editor.title) || title || model}</h1>
        {editor && editor.canCreate !== false && (
          <Can module={module} action="create">
            <Button size="sm" onClick={() => openForm(null)}>New {model}</Button>
          </Can>
        )}
      </div>
      {err ? <Alert variant="error" title="Could not load">{err}</Alert> : null}
      {loading ? (
        <Loader text="Loading…" />
      ) : (
        <Table
          data={tableRows}
          columns={columns}
          pagination
          globalSearch
          withAction={false}
          onRowClick={editor ? (row) => openForm(rows.find((r) => r.id === row.id)) : undefined}
        />
      )}

      {editor && (
        <Dialog isOpen={editing !== null} onClose={() => setEditing(null)} title={editing && editing.id ? `Edit ${model}` : `New ${model}`} size="lg">
          {editing !== null && (
            <div className="space-y-4">
              {formErr ? <Alert variant="error">{formErr}</Alert> : null}
              {editor.fields.map((f) => (
                <FieldInput key={f.name} field={f} value={values[f.name]} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} />
              ))}
              <div className="flex items-center justify-between gap-3 pt-2">
                {editing.id ? (
                  <Can module={module} action="delete">
                    {confirmingDelete ? (
                      <span className="flex items-center gap-2">
                        <Button variant="destructive" loading={saving} onClick={remove}>Yes, delete</Button>
                        <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>Keep</Button>
                      </span>
                    ) : (
                      <Button variant="outline" onClick={() => setConfirmingDelete(true)}>Delete</Button>
                    )}
                  </Can>
                ) : <span />}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                  <Button loading={saving} onClick={save}>Save</Button>
                </div>
              </div>
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
}
